<script setup lang="ts">
import IconClose from '@boats/ui/components/icons/IconClose.vue'

defineProps<{
  /** Where the up-arrow sits along the balloon's top edge. */
  arrow?: 'left' | 'right'
}>()
// mouseover/focusin are stopped so reaching for the X doesn't count as
// opening the hover-driven desktop selector (which would dismiss the toast
// before the click lands).
const emit = defineEmits<{ (event: 'dismiss'): void }>()
</script>

<template>
  <Transition name="app-currency-toast" appear>
    <div
      class="app-currency-toast"
      :class="`app-currency-toast--arrow-${arrow ?? 'right'}`"
      role="status"
      @mouseover.stop
      @focusin.stop
    >
      <span class="app-currency-toast__text">
        {{ $t('string.Choose-your-preferred-store-currency') }}
      </span>
      <button
        type="button"
        class="app-currency-toast__close"
        :aria-label="$t('string.Close')"
        @click.stop="emit('dismiss')"
      >
        <IconClose aria-hidden="true" />
      </button>
    </div>
  </Transition>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

// Per "Currency Selector Concepts" 2b: tint-red fill, yellow hairline, soft
// drop shadow and a rotated-square arrow pointing up at the selector.
$toast-bg: #ffe4e0;
$toast-border: #f4b719;

.app-currency-toast {
  position: absolute;
  top: calc(100% + 0.75rem);
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.5rem 0.5rem 1.25rem;
  border-radius: 0.25rem;
  background: $toast-bg;
  box-shadow:
    inset 0 0 0 1px $toast-border,
    2px 10px 32px rgba(0, 0, 0, 0.08);
  color: $black;
  white-space: nowrap;
  text-transform: none;
  letter-spacing: 0;

  &::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    width: 0.875rem;
    height: 0.875rem;
    background: $toast-bg;
    border-top: 1px solid $toast-border;
    border-left: 1px solid $toast-border;
    border-radius: 2px 0 0 0;
    transform: rotate(45deg);
  }

  &--arrow-right {
    right: -0.75rem;

    &::before {
      right: 2rem;
    }
  }

  &--arrow-left {
    left: 1rem;

    &::before {
      left: 1.25rem;
    }
  }
}

.app-currency-toast__text {
  font-family: $font-poppins;
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.75;
}

.app-currency-toast__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 0;
  border-radius: 0.25rem;
  background: transparent;
  color: $black;
  cursor: pointer;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }

  &:hover {
    background: rgba($black, 0.06);
  }

  &:focus-visible {
    outline: 2px solid $red;
    outline-offset: 1px;
  }
}

.app-currency-toast-enter-active,
.app-currency-toast-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.app-currency-toast-enter-from,
.app-currency-toast-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}
</style>
